/**
 * «ДОБАВИТЬ В СЦЕНУ» РАБОТАЕТ ВСЕГДА, А НЕ ТОЛЬКО ПРИ ОТКРЫТОМ ДЕРЕВЕ.
 *
 * Команда вставки в комнату ставится в общую очередь из каталога, библиотеки и шаблонов, а
 * исполнял её ТОЛЬКО `ApartmentSection` — часть панели «Структура». Панель закрыта (а по
 * умолчанию она закрыта) — команда висит в очереди навсегда: кнопка нажата, ответа нет, в
 * сцене ничего не появляется и никакой ошибки не показано. Вьюпортный путь (не-квартира) при
 * этом работал, поэтому расхождение выглядело как «в квартире каталог не добавляет».
 *
 * Владелец вынесен сюда и живёт в оболочке приложения, которая смонтирована всегда.
 * `ApartmentSection` больше очередь не разбирает — двух владельцев у одной команды быть не
 * должно, даже с защитой `claim`.
 */
import { useEffect } from 'react';
import { toast } from 'sonner';
import { useViewerStore } from '@/shared/store/viewer-store';
import { useUiStore } from '@/shared/store/ui-store';
import { useSceneCommandStore, sceneCommandIdentity, commandIdentityMatches } from '@/shared/store/scene-command-store';
import { useBundleSelector } from '@/shared/api/useBundleSelector';
import { useAddCabinetToRoom, useAddModuleToRoom, useUndo } from '@/shared/api/mutations';
import type { CanonicalEntity, UniversalBundle } from '@/shared/api/types';
import { entityDomain } from '@/features/scene/entityDomain';

type RoomRow = { id: string; code: string; name: string };

/** Комнаты бандла в том виде, в каком их адресует команда вставки: код + собственный id. */
export function roomRowsOfBundle(bundle: UniversalBundle | undefined): RoomRow[] {
  const entities = (bundle?.canonicalScene?.entities ?? []) as CanonicalEntity[];
  const rows: RoomRow[] = [];
  for (const entity of entities) {
    const domain = entityDomain(entity);
    const objectKind = String(domain?.objectKind ?? '').toLowerCase();
    if (String(entity.kind ?? '').toLowerCase() !== 'room' && objectKind !== 'apartment_room') continue;
    const code = String(domain?.roomCode ?? '').trim();
    if (!code) continue;
    rows.push({ id: String(entity.id), code, name: String(entity.name ?? '').trim() });
  }
  return rows;
}

/**
 * Куда класть. Человек, выбравший что-то в комнате, ждёт, что модуль встанет в ЭТУ комнату:
 * иначе шкаф уезжает в первую попавшуюся и его приходится искать.
 */
export function targetRoomCode(
  bundle: UniversalBundle | undefined,
  selectedId: string | null,
): string | null {
  const rooms = roomRowsOfBundle(bundle);
  if (!rooms.length) return null;
  if (selectedId) {
    const entities = (bundle?.canonicalScene?.entities ?? []) as CanonicalEntity[];
    const byId = new Map(entities.map((entity) => [String(entity.id), entity]));
    const selected = byId.get(selectedId);
    const direct = rooms.find((room) => room.id === selectedId);
    if (direct) return direct.code;
    // Выбрана деталь или модуль — комната берётся у их владельца.
    let cursor: CanonicalEntity | undefined = selected;
    for (let depth = 0; cursor && depth < 8; depth += 1) {
      const domain = entityDomain(cursor);
      const roomId = String(domain?.roomId ?? '').trim();
      if (roomId) {
        const owned = rooms.find((room) => room.id === roomId || room.code === roomId);
        if (owned) return owned.code;
      }
      const parentId = String((cursor as { parentId?: unknown }).parentId ?? '');
      cursor = parentId ? byId.get(parentId) : undefined;
    }
  }
  return rooms[0].code;
}

export function useRoomModuleAddCommands(): void {
  const activeModelId = useViewerStore((s) => s.activeModelId);
  const selectedId = useViewerStore((s) => s.selectedId);
  const setSelected = useViewerStore((s) => s.setSelected);
  const setActiveModule = useViewerStore((s) => s.setActiveModule);
  const sceneCommandRevision = useSceneCommandStore((s) => s.revision);
  const bundle = useBundleSelector(activeModelId, (value) => value).data;
  const addModuleToRoom = useAddModuleToRoom(activeModelId);
  const addCabinetToRoom = useAddCabinetToRoom(activeModelId);
  const undo = useUndo(activeModelId);

  useEffect(() => {
    if (!activeModelId || !bundle) return;
    const store = useSceneCommandStore.getState();
    /* VF-FE-14: команда чужой идентичности не адресована этой комнате —
     * владелец 'room' её молча сливает, как и владелец 'scene'. */
    const currentIdentity = sceneCommandIdentity(activeModelId, bundle);
    const command = store.commands.find(
      (item) => item.status === 'pending' && item.kind === 'module.add'
        && item.route === 'room'
        && commandIdentityMatches(item, currentIdentity),
    );
    if (!command || command.kind !== 'module.add') return;
    const stale = store.commands.find((item) => (
      item.status === 'pending' && item.kind === 'module.add' && item.route === 'room'
      && !commandIdentityMatches(item, currentIdentity)
    ));
    if (stale) useSceneCommandStore.getState().complete(stale.id);
    const roomCode = targetRoomCode(bundle, selectedId);
    if (!roomCode) {
      /* Комнат нет — команда адресована не туда; отдаём её вьюпортному владельцу, а не
         молчим: иначе кнопка снова осталась бы без ответа. */
      store.rerouteModuleAdd(command.id, 'viewport');
      return;
    }
    if (!store.claim(command.id, 'room')) return;

    const payload = command.payload;
    const run = async () => {
      const added = payload.cabinetSpec
        ? await addCabinetToRoom.mutateAsync({ ...payload.cabinetSpec, roomCode, name: payload.name })
        : await addModuleToRoom.mutateAsync({ roomCode, catalogId: payload.catalogId, name: payload.name });
      if (!commandIdentityMatches({ identity: currentIdentity }, sceneCommandIdentity(
        useViewerStore.getState().activeModelId, null,
      ))) return;
      /* Вставленный модуль становится выбранным: следующий шаг — габарит, место,
         наполнение — относится именно к нему. */
      const instanceId = typeof (added as { instanceId?: unknown })?.instanceId === 'string'
        ? String((added as { instanceId?: unknown }).instanceId)
        : '';
      if (instanceId) {
        setSelected(instanceId);
        setActiveModule(instanceId);
      }
      /* VIS-12: «куда встал модуль» — комната·габариты·детали названы словами,
         а следующие шаги (наполнить / передумать) — одной кнопкой каждый.
         «Переместить» отдельной кнопки не имеет: честный перенос — это drag
         выделенного модуля, кнопка-обещание без механизма лгала бы. */
      const roomName = roomRowsOfBundle(bundle).find((room) => room.code === roomCode)?.name || roomCode;
      const dims = payload.dimensionsMm;
      const facts = [
        roomName,
        dims ? `${dims.width}×${dims.height}×${dims.depth} мм` : '',
        typeof (added as { entitiesAdded?: unknown }).entitiesAdded === 'number'
          ? `${(added as { entitiesAdded: number }).entitiesAdded} деталей` : '',
      ].filter(Boolean).join(' · ');
      toast.success(`Модуль добавлен: ${payload.name}`, {
        description: facts,
        duration: 10_000,
        action: { label: 'Наполнить', onClick: () => useUiStore.getState().openLeft('fill') },
        cancel: { label: 'Отменить', onClick: () => undo.mutate() },
      });
    };
    void run()
      .then(() => useSceneCommandStore.getState().complete(command.id))
      .catch((error) => useSceneCommandStore.getState().fail(command.id, error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModelId, bundle, selectedId, sceneCommandRevision]);
}
